/**
 * Joining a league (docs/game-design.md §7.7–7.8): a newly registered fighter takes over a bot's
 * place in one of the two lowest divisions of their country, mid-season, with that place's
 * record. Hattrick's fill order: the series with the fewest humans first, until every series has
 * 4; then the fullest, so series fill up with humans. A fighter back from inactivity rejoins the
 * lowest division.
 */
import { LEAGUES } from '../config/leagues';
import type { PlaceRef } from './season';

export interface SeriesFill {
  division: number;
  number: number;
  humans: number;
  /** Slots a bot holds, the lowest on the table first (the place a newcomer takes). */
  botSlots: readonly number[];
}

/**
 * Whether the player holding a place fights its fight of `day`: from `joinedDay`, the first day
 * whose fight hadn't begun when they took the place. The place's bot fights the days before, so a
 * fighter placed during a day's live window, or after it, starts the next day (D50: that day has
 * no Fight now and no Live for them). Null: nobody but the bot.
 */
export function fightsOn(joinedDay: number | null, day: number): boolean {
  return joinedDay !== null && day >= joinedDay;
}

/** The place a new fighter takes, or null when no bot holds one in the divisions allowed. */
export function pickJoinPlace(series: readonly SeriesFill[], divisions: number, lowestOnly = false): PlaceRef | null {
  const top = lowestOnly ? divisions : Math.max(1, divisions - 1);
  const open = series.filter((s) => s.division >= top && s.division <= divisions && s.botSlots.length > 0);
  if (!open.length) return null;
  const even = open.some((s) => s.humans < LEAGUES.joinEvenUpTo);
  open.sort((a, b) => (even ? a.humans - b.humans : b.humans - a.humans) || b.division - a.division || a.number - b.number);
  const s = open[0]!;
  return { division: s.division, number: s.number, slot: s.botSlots[0]! };
}
